'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { TickerItem } from '../../types'
import { ConnectorManager } from '../../ConnectorManager'
import { isMarketOpen, getTimeUntilMarketOpens } from '../utils/marketHours'

interface UseGrowwPollingProps {
  onTickerUpdate: (tickers: TickerItem[]) => void
  pollingInterval?: number // in milliseconds
  symbols?: string[] // visible symbols to prioritize
  batchSize?: number // number of symbols per batch
}

// Client-side cache for ticker data
interface TickerCache {
  data: TickerItem;
  timestamp: number;
}

// Global cache that persists between component instances
const GLOBAL_CACHE: { [key: string]: TickerCache } = {};

// Cache TTL in milliseconds - 30 seconds
const CACHE_TTL = 30000;

// Market-aware polling intervals
const MARKET_OPEN_INTERVAL = 15000; // 15 seconds when market is open
const MARKET_CLOSED_INTERVAL = 60000; // 60 seconds when market is closed
const AFTER_HOURS_INTERVAL = 30000; // 30 seconds during after-hours

// Global coordination to reduce duplicate polling
interface PollCoordinator {
  lastPollTime: number;
  isPolling: boolean;
  activePollers: number;
  symbolsBeingPolled: Set<string>;
  pendingSymbols: Set<string>;
  subscribers: Set<(data: TickerItem[]) => void>;
}

const GLOBAL_POLL_COORDINATOR: PollCoordinator = {
  lastPollTime: 0,
  isPolling: false,
  activePollers: 0,
  symbolsBeingPolled: new Set(),
  pendingSymbols: new Set(),
  subscribers: new Set(),
};

// Minimum time between polling requests in milliseconds - to prevent hammering the API
const MIN_POLL_INTERVAL = 5000;

// Global shared fetch - prevents duplicate API calls for the same data
async function globalFetchBatch(
  symbols: string[],
  batchSize: number
): Promise<TickerItem[]> {
  // If already polling these symbols, don't fetch them again
  const uniqueSymbols = symbols.filter(s => !GLOBAL_POLL_COORDINATOR.symbolsBeingPolled.has(s));
  
  if (uniqueSymbols.length === 0) {
    // No new symbols to fetch
    return [];
  }
  
  // Add symbols to being polled set
  uniqueSymbols.forEach(s => GLOBAL_POLL_COORDINATOR.symbolsBeingPolled.add(s));
  
  try {
    const connector = ConnectorManager.getInstance().getConnector('groww');
    if (!connector) return [];
    
    const options = uniqueSymbols.length > 0 ? { symbols: uniqueSymbols.slice(0, batchSize) } : undefined;
    const rawTickersData = await connector.getTickers(options);
    
    // Transform the data to match the expected TickerItem format
    const transformedData = rawTickersData.map(ticker => ({
      symbol: ticker.symbol.toLowerCase(),
      originalSymbol: ticker.symbol,
      price: ticker.price || 0,
      priceChangePercent: ticker.priceChangePercent || 0,
      volume: ticker.volume || 0,
      exchange: ticker.exchange || 'NSE'
    }));
    
    // Cache each ticker individually for better reuse
    transformedData.forEach(ticker => {
      const cacheKey = ticker.originalSymbol;
      GLOBAL_CACHE[cacheKey] = {
        data: ticker,
        timestamp: Date.now()
      };
    });
    
    return transformedData;
  } catch (err) {
    console.error('Error fetching batch:', err);
    return [];
  } finally {
    // Remove symbols from being polled set
    uniqueSymbols.forEach(s => GLOBAL_POLL_COORDINATOR.symbolsBeingPolled.delete(s));
  }
}

// Progressive backoff mechanism to reduce polling frequency over time
function getProgressiveInterval(baseInterval: number, idleTime: number): number {
  const minutes = idleTime / (1000 * 60);
  
  if (minutes < 2) return baseInterval; // Regular interval for first 2 minutes
  if (minutes < 5) return baseInterval * 1.5; // 1.5x after 2 minutes
  if (minutes < 10) return baseInterval * 2; // 2x after 5 minutes
  if (minutes < 30) return baseInterval * 3; // 3x after 10 minutes
  
  return baseInterval * 4; // 4x after 30 minutes
}

/**
 * Custom hook for polling Groww market data at regular intervals
 * This replaces WebSocket functionality since Groww doesn't provide WebSockets
 */
export function useGrowwPolling({
  onTickerUpdate,
  pollingInterval = MARKET_OPEN_INTERVAL, // Default to market-open interval
  symbols = [],
  batchSize = 50 // Default batch size
}: UseGrowwPollingProps) {
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [effectiveInterval, setEffectiveInterval] = useState(pollingInterval)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const connectorRef = useRef(ConnectorManager.getInstance().getConnector('groww'))
  
  // Visibility tracking
  const isComponentVisible = useRef(true)
  
  // Store the current symbols for batching - ensures stability between renders
  const symbolsRef = useRef<string[]>([])
  
  // Track last interaction time to apply progressive backoff
  const lastInteractionRef = useRef(Date.now())
  
  // Track mounted state
  const isMountedRef = useRef(true)
  
  // Register as a poller when component mounts
  useEffect(() => {
    GLOBAL_POLL_COORDINATOR.activePollers++;
    
    return () => {
      if (GLOBAL_POLL_COORDINATOR.activePollers > 0) {
        GLOBAL_POLL_COORDINATOR.activePollers--;
      }
    };
  }, []);
  
  // Update the symbols ref when symbols change, but don't trigger re-renders
  useEffect(() => {
    // Only update if there's a meaningful change to avoid unnecessary polling resets
    if (symbols && JSON.stringify(symbolsRef.current) !== JSON.stringify(symbols)) {
      console.log(`Updating polling symbols: ${symbols.length} symbols`);
      symbolsRef.current = symbols;
      
      // Add new symbols to pending set
      symbols.forEach(symbol => GLOBAL_POLL_COORDINATOR.pendingSymbols.add(symbol));
      
      // Reset last interaction time to use regular polling interval
      lastInteractionRef.current = Date.now();
    }
  }, [symbols]);
  
  // Track visibility to avoid polling when page is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      isComponentVisible.current = document.visibilityState === 'visible';
      
      if (isComponentVisible.current) {
        // Reset last interaction time when page becomes visible
        lastInteractionRef.current = Date.now();
        
        // Force an immediate data fetch when becoming visible again
        fetchTickers();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Adjust polling interval based on market hours
  const updatePollingInterval = useCallback(() => {
    // Get base interval based on market hours
    let baseInterval = MARKET_OPEN_INTERVAL;
    
    if (!isMarketOpen()) {
      const timeUntilOpen = getTimeUntilMarketOpens();
      baseInterval = timeUntilOpen < 60 * 60 * 1000 ? AFTER_HOURS_INTERVAL : MARKET_CLOSED_INTERVAL;
    }
    
    // Apply progressive backoff based on idle time
    const idleTime = Date.now() - lastInteractionRef.current;
    const adjusted = getProgressiveInterval(baseInterval, idleTime);
    
    if (adjusted !== effectiveInterval) {
      console.log(`Adjusting polling interval to ${adjusted}ms`);
      setEffectiveInterval(adjusted);
    }
    
    return adjusted;
  }, [effectiveInterval]);
  
  // Mouse activity detection to reset progressive backoff
  useEffect(() => {
    function handleUserActivity() {
      lastInteractionRef.current = Date.now();
      updatePollingInterval();
    }
    
    // Reset backoff on mouse movement and clicks
    document.addEventListener('mousemove', handleUserActivity, { passive: true });
    document.addEventListener('click', handleUserActivity, { passive: true });
    
    return () => {
      document.removeEventListener('mousemove', handleUserActivity);
      document.removeEventListener('click', handleUserActivity);
    };
  }, [updatePollingInterval]);
  
  // Check ticker cache for a given symbol
  const checkCache = useCallback((symbol: string): TickerItem | null => {
    const cached = GLOBAL_CACHE[symbol];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }
    return null;
  }, []);
  
  // Function to fetch ticker data in batches
  const fetchTickers = useCallback(async (force = false) => {
    // Skip if not visible or not mounted
    if (!isComponentVisible.current || !isMountedRef.current) {
      return;
    }
    
    // Throttle API calls using global coordinator
    const now = Date.now();
    const timeSinceLastPoll = now - GLOBAL_POLL_COORDINATOR.lastPollTime;
    
    // Don't poll too frequently, unless forced
    if (!force && timeSinceLastPoll < MIN_POLL_INTERVAL) {
      return;
    }
    
    // If another component is already polling, wait for it
    if (GLOBAL_POLL_COORDINATOR.isPolling) {
      return;
    }
    
    GLOBAL_POLL_COORDINATOR.isPolling = true;
    GLOBAL_POLL_COORDINATOR.lastPollTime = now;
    
    // Make the coordinator available to other components via window
    if (typeof window !== 'undefined') {
      window.GLOBAL_POLL_COORDINATOR = GLOBAL_POLL_COORDINATOR;
    }
    
    try {
      // Collect all symbols we need data for
      const visibleSymbols = symbolsRef.current;
      
      // First check cache for each symbol
      const cachedResults: TickerItem[] = [];
      const symbolsToFetch: string[] = [];
      
      visibleSymbols.forEach(symbol => {
        const cached = checkCache(symbol);
        if (cached) {
          cachedResults.push(cached);
        } else {
          symbolsToFetch.push(symbol);
        }
      });
      
      // Add pending symbols from other components
      GLOBAL_POLL_COORDINATOR.pendingSymbols.forEach(symbol => {
        if (!symbolsToFetch.includes(symbol)) {
          symbolsToFetch.push(symbol);
        }
      });
      
      // Clear pending symbols
      GLOBAL_POLL_COORDINATOR.pendingSymbols.clear();
      
      // If we have cached results, use them immediately
      if (cachedResults.length > 0) {
        onTickerUpdate(cachedResults);
      }
      
      // If no symbols to fetch, we're done
      if (symbolsToFetch.length === 0) {
        setIsConnected(true);
        return;
      }
      
      // Process in batches to reduce API load
      const batchCount = Math.ceil(symbolsToFetch.length / batchSize);
      
      for (let i = 0; i < batchCount; i++) {
        const batchStart = i * batchSize;
        const batchSymbols = symbolsToFetch.slice(batchStart, batchStart + batchSize);
        
        // Fetch this batch using the global fetch function
        const tickerBatch = await globalFetchBatch(batchSymbols, batchSize);
        
        // Update state if we have data and component is still mounted
        if (tickerBatch.length > 0 && isMountedRef.current) {
          onTickerUpdate(tickerBatch);
        }
        
        // Small delay between batches to avoid overloading
        if (i < batchCount - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
      
      // Success!
      setIsConnected(true);
      setError(null);
      
    } catch (err) {
      console.error('Error in poll cycle:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch ticker data');
      setIsConnected(false);
    } finally {
      // Release the lock
      GLOBAL_POLL_COORDINATOR.isPolling = false;
    }
  }, [batchSize, onTickerUpdate, checkCache]);
  
  // Check market hours periodically and update polling interval
  useEffect(() => {
    // Initial check
    updatePollingInterval();
    
    // Set up timer to check market hours every minute
    const timer = setInterval(() => {
      updatePollingInterval();
    }, 60000);
    
    return () => clearInterval(timer);
  }, [updatePollingInterval]);
  
  // Start polling
  useEffect(() => {
    isMountedRef.current = true;
    
    // Initial fetch (without force to use cache if available)
    fetchTickers(false);
    
    // Set up polling with the current effective interval
    const startPolling = () => {
      // Clear any existing timer
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      
      // Create new timer
      pollingIntervalRef.current = setInterval(() => {
        fetchTickers(false);
      }, effectiveInterval);
    };
    
    startPolling();
    
    // Update timer when interval changes
    return () => {
      isMountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchTickers, effectiveInterval]);

  return { isConnected, error };
}